import {
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
} from "react";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  BorderOutlined,
  CloseOutlined,
  PaperClipOutlined,
  PlusOutlined,
} from "@ant-design/icons";

import NeoVoiceInput from "../../../pages/Neo/NeoVoiceInput";
import { useAutoGrowingTextarea } from "../../hooks/useAutoGrowingTextarea";
import styles from "./styles.module.scss";

interface CopilotComposerProps {
  value: string;
  streaming: boolean;
  selectedClinicId?: number;
  onChange: (value: string) => void;
  /** Resolves false when the message never went out (files are restored). */
  onSend: (files: File[]) => Promise<boolean>;
  onStop: () => void;
  showScrollToBottom?: boolean;
  onScrollToBottom?: () => void;
  placeholder?: string;
  /** Placeholder copy that rotates with the same animation as Home. */
  rotatingPlaceholders?: readonly string[];
  /** Starter questions shown as chips above the composer. */
  suggestions?: readonly string[];
  onSuggestion?: (text: string) => void;
  /** Tighter padding for narrow hosts (the Ask Circadia AI panel). */
  compact?: boolean;
}

const ATTACHMENT_ACCEPT =
  ".pdf,.png,.jpg,.jpeg,.webp,.docx,.txt,.md,.csv," +
  "application/pdf,image/png,image/jpeg,image/webp," +
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
  "text/plain,text/markdown,text/csv";

const ACCEPTED_EXTENSIONS = new Set(
  ATTACHMENT_ACCEPT.split(",")
    .filter((entry) => entry.startsWith("."))
    .map((entry) => entry.slice(1))
);

const isAcceptedFile = (file: File): boolean =>
  ACCEPTED_EXTENSIONS.has(file.name.split(".").pop()?.toLowerCase() ?? "");

const CopilotComposer = ({
  value,
  streaming,
  selectedClinicId,
  onChange,
  onSend,
  onStop,
  showScrollToBottom = false,
  onScrollToBottom,
  placeholder = "Message Neo…",
  rotatingPlaceholders,
  suggestions,
  onSuggestion,
  compact = false,
}: CopilotComposerProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [voiceActive, setVoiceActive] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useAutoGrowingTextarea(value);
  const rotatingPlaceholder =
    rotatingPlaceholders && rotatingPlaceholders.length > 0
      ? rotatingPlaceholders[placeholderIndex % rotatingPlaceholders.length]
      : null;

  useEffect(() => {
    setPlaceholderIndex(0);
    if (
      !rotatingPlaceholders ||
      rotatingPlaceholders.length < 2 ||
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
    ) {
      return;
    }

    const interval = window.setInterval(() => {
      setPlaceholderIndex(
        (current) => (current + 1) % rotatingPlaceholders.length
      );
    }, 4000);

    return () => window.clearInterval(interval);
  }, [rotatingPlaceholders]);

  const submit = async () => {
    if (!value.trim() && files.length === 0) return;
    // Clear the chips as soon as the message goes out, not after the reply.
    const sending = files;
    setFiles([]);
    if (!(await onSend(sending))) {
      setFiles((current) => [...sending, ...current]);
    }
  };
  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (streaming || !event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    setDragging(true);
  };
  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    setDragging(false);
    if (streaming) return;
    event.preventDefault();
    const dropped = Array.from(event.dataTransfer.files).filter(isAcceptedFile);
    if (dropped.length) setFiles((current) => [...current, ...dropped]);
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (!streaming && (value.trim() || files.length > 0)) void submit();
    }
  };

  return (
    <div
      className={`${styles.composerArea} ${
        compact ? styles.composerAreaCompact : ""
      }`}
    >
      {showScrollToBottom && (
        <>
          <span className={styles.composerTopFade} aria-hidden />
          <button
            type="button"
            className={styles.scrollToBottomButton}
            onClick={onScrollToBottom}
            aria-label="Scroll to latest message"
            title="Scroll to bottom"
          >
            <ArrowDownOutlined />
          </button>
        </>
      )}
      {suggestions && suggestions.length > 0 && onSuggestion && (
        <div className={styles.composerSuggestions}>
          {suggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              disabled={streaming}
              onClick={() => onSuggestion(suggestion)}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
      <div
        className={`${styles.composer} ${
          dragging ? styles.composerDragging : ""
        }`}
        onDragOver={handleDragOver}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node)) {
            setDragging(false);
          }
        }}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          className={styles.fileInput}
          onChange={(event) => {
            setFiles((current) => [
              ...current,
              ...Array.from(event.target.files ?? []),
            ]);
            event.target.value = "";
          }}
        />
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={rotatingPlaceholder ? undefined : placeholder}
          rows={1}
          aria-label="Message Neo"
          disabled={voiceActive}
        />
        {!value && rotatingPlaceholder && (
          <span
            key={`${placeholderIndex}-${rotatingPlaceholder}`}
            className={styles.rotatingPlaceholder}
            aria-hidden="true"
          >
            {rotatingPlaceholder}
          </span>
        )}
        {files.length > 0 && (
          <div className={styles.composerAttachments}>
            {files.map((file, index) => (
              <span key={`${file.name}-${file.size}-${index}`}>
                <PaperClipOutlined />
                <span>{file.name}</span>
                <button
                  type="button"
                  onClick={() =>
                    setFiles((current) =>
                      current.filter((_, fileIndex) => fileIndex !== index)
                    )
                  }
                  aria-label={`Remove ${file.name}`}
                >
                  <CloseOutlined />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className={styles.composerFooter}>
          <div className={styles.composerPrimaryActions}>
            <button
              type="button"
              className={styles.composerIconButton}
              disabled={streaming || voiceActive}
              onClick={() => fileInputRef.current?.click()}
              title="Add context"
              aria-label="Add context"
            >
              <PlusOutlined />
            </button>
            {!streaming && (
              <NeoVoiceInput
                onTranscript={(text) =>
                  onChange(value.trim() ? `${value.trimEnd()} ${text}` : text)
                }
                disabled={voiceActive}
                selectedClinicId={selectedClinicId}
                onActiveChange={setVoiceActive}
                buttonShape="default"
                variant="compact"
              />
            )}
            <button
              type="button"
              className={styles.sendButton}
              disabled={
                !streaming &&
                ((!value.trim() && files.length === 0) || voiceActive)
              }
              onClick={streaming ? onStop : () => void submit()}
              aria-label={streaming ? "Stop generating" : "Send message"}
            >
              {streaming ? <BorderOutlined /> : <ArrowUpOutlined />}
            </button>
          </div>
        </div>
      </div>
      <p>Neo may make mistakes. Always verify clinical information.</p>
    </div>
  );
};

export default CopilotComposer;
